import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class BankAccountsService {
  private baseApi: string = environment.apiUrl + "/bank/accounts/";

  constructor(private http: HttpClient) { }

  public getBankAccounts(): Observable<BankAccount[]> {
    return this.http.get<BankAccount[]>(this.baseApi);
  }

  public getBankAccount(bankAccountId: string): Observable<BankAccount> {
    return this.http.get<BankAccount>(this.baseApi + bankAccountId);
  }

  public getProviderCredentials(): Observable<ProviderCredential[]> {
    return this.http.get<ProviderCredential[]>(this.baseApi + "credentials");
  }

  public getTransactionsByAccount(bankAccountId: string): Observable<BankTransaction[]> {
    return this.http.get<BankTransaction[]>(this.baseApi + bankAccountId + "/transactions");
  }

  public getRecentTransactionsByAccount(bankAccountId: string, limit: number = 100): Observable<BankTransaction[]> {
    return this.http.get<BankTransaction[]>(this.baseApi + bankAccountId + "/transactions/recent?limit=" + limit);
  }

  public createUpdateProviderCredential(createRequest: CreateCredentialRequest): Observable<CreateCredentialResponse> {
    return this.http.post<CreateCredentialResponse>(this.baseApi + "credentials", createRequest)
      .pipe(catchError(this.handleError));
  }

  public provisionProviderAccount(credId: string, provisionRequest: ProvisionAccountsRequest): Observable<ProvisionAccountsResponse> {
    return this.http.post<ProvisionAccountsResponse>(this.baseApi + "credentials/" + credId + "/provider-accounts", provisionRequest)
      .pipe(catchError(this.handleError));
  }

  public getEnrichmentRulesets(): Observable<EnrichmentRuleset[]> {
    return this.http.get<EnrichmentRuleset[]>(this.baseApi + "enrichment/rulesets");
  }

  public createRuleset(createRequest: CreateRulesetRequest): Observable<EnrichmentRuleset> {
    return this.http.post<EnrichmentRuleset>(this.baseApi + "enrichment/rulesets", createRequest)
      .pipe(catchError(this.handleError));
  }

  public udpateRuleset(rulset: EnrichmentRuleset): Observable<EnrichmentRuleset> {
    return this.http.put<EnrichmentRuleset>(this.baseApi + "enrichment/rulesets", rulset)
    .pipe(catchError(this.handleError));
  }

  public deleteRuleset(rulsetId: string): Observable<any> {
    return this.http.delete(this.baseApi + "enrichment/rulesets/" + rulsetId);
  }

  public testRules(rules: EnrichmentRule[]): Observable<EnrichmentRuleTestResult[]> {
    return this.http.post<EnrichmentRuleTestResult[]>(this.baseApi + "enrichment/rulesets/rules/test", rules);
  }

  public getIncomeTypeText(incomeType: string): string {
    if(INCOME_TYPES[incomeType]) {
      return INCOME_TYPES[incomeType];
    }

    return incomeType;
  }

  public getTransactionTypeText(txnType: string): string {
    if(TRANSACTION_TYPES[txnType]) {
      return TRANSACTION_TYPES[txnType];
    }

    return txnType;
  }

  public executeAccountsResyncJob(): Observable<JobAccountsResyncResponse> {
    return this.http.post<JobAccountsResyncResponse>(this.baseApi + "jobs/resync", undefined);
  }

  // public testRules(rules: EnrichmentRule[]): Observable<EnrichmentRuleTestResult[]> {
  //   return new Observable<EnrichmentRuleTestResult[]>(observer => {
  //     var result: EnrichmentRuleTestResult[] = new Array<EnrichmentRuleTestResult>();

  //     var before: BankTransaction = new BankTransaction();
  //     before.Amount = 100;
  //     before.TransactionDate = new Date("2023-09-22");
  //     before.Description = "Test Transaction";
  //     before.TransactionType = "CREDIT";

  //     var after = JSON.parse(JSON.stringify(before)) as BankTransaction;
  //     after.TransactionType = "TRANSFER";
  //     after.TransactionCategory = "Test Category";
  //     after.TransactionCategoryGroup = "Test Group";
      
  //     result.push({
  //       AccountId: "00000000-0000-0000-0000-000000000000",
  //       AccountName: "Test Account",
  //       AccountStatus: "Active",
  //       AccountType: "Current",
  //       BankLogoUrl: "about:blank",
  //       BankName: "Test Bank",
  //       After: after,
  //       Before: before
  //     });

  //     setTimeout(() => observer.next(result), 5000);
  //   });
  // }


  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(
        `Backend returned code ${error.status}, body was: `, error.error);
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }
}


export class BankAccount {
  constructor() {
    this.BalanceRefreshedAt = new Date();
    this.CreatedAt = new Date();
    this.UpdatedAt = new Date();
    this.LastSyncedAt = new Date();
  }

  public Id: string = "";
  public IncludeInSync: boolean = true;
  public Name: string = "";
  public BankName: string = "";
  public BankLogoUrl: string = "";
  public Status: string = "";
  public FormattedAccount: string = "";
  public AccountType: string = "";
  public CurrentBalance: number = 0;
  public AvailableBalance: number | undefined;
  public BalanceRefreshedAt: Date;
  public TransactionsRefreshedAt: Date | undefined;
  public CreditLimit: number | undefined;
  public IsOverdrawn: boolean | undefined;
  public Currency: string = "";
  public CreatedAt: Date;
  public UpdatedAt: Date;
  public LastSyncedAt: Date;
  public LastSyncedTransactionFromDate: Date | undefined;
}

export class ProviderCredential {
  constructor() {
    this.CreatedAt = new Date();
    this.UpdatedAt = new Date();
  }

  public Id: string = "";
  public Provider: string = "";
  public Identifer: string = "";
  public CreatedAt: Date;
  public UpdatedAt: Date;
}

export class CreateCredentialRequest {
  public AppSecret: string = "";
  public UserSecret: string = "";
}

export class CreateCredentialResponse {
  constructor() {
    this.Accounts = new Array<AccountListEntry>();
  }

  public Success: boolean = true;
  public Accounts: AccountListEntry[];
  public CredentialId: string = "";
}

export class AccountListEntry {
  public ProviderAccountId: string = "";
  public Name: string = "";
  public BankName: string = "";
  public BankLogoUrl: string = "";
  public FormattedAccountNumber: string = "";
  public HolderName: string = "";
}

export class ProvisionAccountsRequest {
  constructor() {
    this.ProviderAccountIds = new Array<string>();
  }

  public ProviderAccountIds: string[];
}

export class ProvisionAccountsResponse {
  public Success: boolean = true;
  public RequestId: string = "";
  public ErrorMessage: string | undefined;
}

export class BankTransaction {
  constructor() {
    this.TransactionDate = new Date();
    this.LastSyncedAt = new Date();
    this.Tags = new Array<string>();
  }

  public Id: string = "";
  public AccountId: string = "";
  public TransactionDate: Date;
  public Description: string = "";
  public CardUsed: string = "";
  public Amount: number = 0;  
  public Balance: number = 0;
  public TransactionType: string = "";
  public TransactionCategory: string = "";
  public TransactionCategoryGroup: string = "";
  public MerchantName: string = "";
  public MerchantWebsite: string = "";
  public MerchantLogo: string = "";
  public Conversion: BankTransactionConversion | undefined;
  public LastSyncedAt: Date;
  public Tags: string[];
  public IncomeSource: string = "";
  public IncomeType: string = "";
}

export class BankTransactionConversion {
  public Amount: number = 0;
  public Currency: string = "";
  public Rate: number = 0;
}

export class EnrichmentRuleActionExpressionIncome {
  public Name: string | undefined;
  public IncomeType: string | undefined;
}


export class EnrichmentRuleActionExpressionCategory {
  public GroupId: string | undefined;
  public GroupText: string | undefined;
  public CategoryId: string | undefined;
  public CategoryText: string | undefined;
}

export class EnrichmentRuleAction {
  public ActionType: string = "";
  public ActionExpression: string | undefined;
}

export class EnrichmentRule {

  constructor() {
    this.Actions = new Array<EnrichmentRuleAction>();
  }

  public Id: string | undefined;
  public CreatedAt: Date | undefined;
  public UpdatedAt: Date | undefined;
  public Status: string = "";
  public RuleType: string = "";
  public RuleExpression: string = "";
  public Actions: EnrichmentRuleAction[];
}

export class EnrichmentRuleset {

  constructor() {
    this.Rules = new Array<EnrichmentRule>();
  }

  public Id: string = "";
  public CreatedAt: Date | undefined;
  public UpdatedAt: Date | undefined;
  public DisplayName: string = "";
  public Rules: EnrichmentRule[];
}

export class CreateRulesetRequest {
  public DisplayName: string = "";
}

export class EnrichmentRuleTestResult {

  constructor() {
    this.After = new BankTransaction();
    this.Before = new BankTransaction();
  }

  public AccountId: string = "";
  public AccountName: string = "";
  public AccountStatus: string = "";
  public AccountType: string = "";
  public BankLogoUrl: string = "";
  public BankName: string = "";
  public After: BankTransaction;
  public Before: BankTransaction;
}

export class JobAccountsResyncResponse {
  public RequestId: string = "";
  public Status: string = "";
  public Accounts: BankAccountListItem[];

  constructor() {
    this.Accounts = new Array<BankAccountListItem>();
  }
}

export class BankAccountListItem {
  public Id: string = "";
  public Name: string = "";
  public BankName: string = "";
  public BankLogoUrl: string = "";
  public Status: string = "";
  public FormattedAccount: string = "";
  public AccountType: string = "";
  public Currency: string = "";
}

export const TRANSACTION_TYPES: Record<string, string> = {
  "TRANSFER": "Transfer between accounts",
  "PAYMENT": "Payment to an external account",
  "STANDING ORDER": "Automatic payment",
  "INTEREST": "Interest payment",
  "FEE": "Fee payment to the account provider",
  "TAX": "Tax payment",
  "CREDIT CARD": "Credit card payment",
  "DIRECT DEBIT": "Direct debit payment",
  "DIRECT CREDIT": "Direct credit (someone paying into the account)",
  "LOAN": "Loan payment",
  "CREDIT": "Credit",
  "DEBIT": "Debit",
  "ATM": "ATM deposit or withdrawal",
  "EFTPOS": "Payment made via EFTPOS"
};

export const INCOME_TYPES: Record<string, string> = {
  "SALARY": "Salary payment",
  "INTEREST": "Interest earnt",
  "BENEFIT": "Govt benefit payment",
  "RENT": "Rental income",
  "OFFSHORE": "Money received from an overseas account",
  "DEPOSIT": "Money received to reserve or use a certain goods or services"
};